import { useCallback, useEffect, useRef, useState } from 'react'

import { CastPanel } from './components/CastPanel'
import { ChannelsPanel } from './components/ChannelsPanel'
import { LinkForm } from './components/LinkForm'
import { Login } from './components/Login'
import { Metrics, type Sample } from './components/Metrics'
import { Player } from './components/Player'
import { SchedulePanel } from './components/SchedulePanel'
import { api, ApiError, type PlaybackStats, type Session } from './lib/api'
import { clearLaunchUrl, readLaunchLink } from './lib/deeplink'
import { rememberRecent } from './lib/recents'

/** Two minutes of history at one sample per poll. */
const HISTORY_LENGTH = 60

type AuthState = 'checking' | 'required' | 'ok'

export default function App() {
  const [auth, setAuth] = useState<AuthState>('checking')
  const [session, setSession] = useState<Session | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** Local playback is muted while a Chromecast has the stream, to avoid echo. */
  const [muteLocal, setMuteLocal] = useState(false)
  const [playback, setPlayback] = useState<PlaybackStats | null>(null)
  const [history, setHistory] = useState<Sample[]>([])
  const sessionIdRef = useRef<string | null>(null)

  sessionIdRef.current = session?.id ?? null

  const checkAuth = useCallback(async () => {
    try {
      const me = await api.me()
      setAuth(!me.authRequired || me.authenticated ? 'ok' : 'required')
    } catch {
      // Cannot reach the API at all; show the login rather than a blank app.
      setAuth('required')
    }
  }, [])

  useEffect(() => {
    void checkAuth()
  }, [checkAuth])

  // Polling keeps stats fresh and, just as importantly, tells the server the
  // session is still wanted so the idle reaper leaves it alone.
  useEffect(() => {
    if (!session) return
    const timer = setInterval(async () => {
      const id = sessionIdRef.current
      if (!id) return
      try {
        const next = await api.getSession(id)
        setSession(next)
        setHistory((prev) =>
          [
            ...prev,
            {
              downKbps: (next.stat?.speedDown ?? 0) * 8,
              speed: next.progress?.speed ?? 0,
            },
          ].slice(-HISTORY_LENGTH),
        )
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          setAuth('required')
          setSession(null)
          return
        }
        if (err instanceof ApiError) setError(err.message)
      }
    }, 2000)
    return () => clearInterval(timer)
  }, [session?.id])

  const start = useCallback(async (link: string) => {
    setBusy(true)
    setError(null)
    setHistory([])
    try {
      const created = await api.createSession(link)
      setSession(created)
      rememberRecent(link)
      if (created.status === 'error') setError(created.error)
    } catch (err) {
      setSession(null)
      setError(err instanceof ApiError ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [])

  const stop = useCallback(async () => {
    const id = sessionIdRef.current
    if (!id) return
    setBusy(true)
    try {
      await api.stopSession(id)
    } catch {
      // Already gone server-side; clearing local state is the right outcome.
    } finally {
      setSession(null)
      setError(null)
      setHistory([])
      setBusy(false)
    }
  }, [])

  // Launched from the Android share sheet, a protocol handler or a deep link.
  const launched = useRef(false)
  useEffect(() => {
    if (auth !== 'ok' || launched.current) return
    launched.current = true
    const link = readLaunchLink()
    if (!link) return
    clearLaunchUrl()
    void start(link)
  }, [auth, start])

  const playable = session?.status === 'ready' ? session.hlsPath : null

  if (auth === 'checking') return <div className="app app--login" />
  if (auth === 'required') return <Login onSuccess={() => setAuth('ok')} />

  return (
    <div className="app">
      <header className="app__header">
        <img src="/icon.svg" alt="" width={28} height={28} />
        <h1>AceCast</h1>
        <span className="app__tagline">AceStream → HLS → Chromecast</span>
        <button
          className="btn btn--ghost app__logout"
          onClick={() =>
            void api
              .logout()
              .catch(() => {})
              .then(() => {
                setSession(null)
                setAuth('required')
              })
          }
        >
          Surt
        </button>
      </header>

      <LinkForm busy={busy} onSubmit={(link) => void start(link)} onStop={() => void stop()} hasSession={!!session} />

      {error && <p className="app__error">{error}</p>}

      {session && (
        <>
          <Metrics session={session} playback={playback} history={history} />
          {session.status === 'starting' && (
            <p className="app__hint">
              Connectant amb el swarm AceStream. Amb pocs peers pot trigar fins a un minut a produir el primer segment.
            </p>
          )}
          {session.status === 'error' && session.ffmpegLog.length > 0 && (
            <pre className="app__log">{session.ffmpegLog.join('\n')}</pre>
          )}
        </>
      )}

      <Player src={playable} muted={muteLocal} onStats={setPlayback} />

      <label className="toggle">
        <input type="checkbox" checked={muteLocal} onChange={(e) => setMuteLocal(e.target.checked)} />
        Silencia la reproducció local (útil mentre emets al Chromecast)
      </label>

      <CastPanel session={session} />

      <ChannelsPanel onPlay={(link) => void start(link)} />

      <SchedulePanel onPlay={(link) => void start(link)} />
    </div>
  )
}
